// src/lib/glossary.ts — ko-admin#214, the operator's vocabulary (spec «Panel claro» C.1 and C.3).
//
// The panel named things after how the backend is built: `escrow`, `outbox`,
// `clawback`, `RESUELTA · COMPRADOR`. An operator had to know the internals to
// read a screen. What lives here is the other side of that: the words a person
// running the platform recognises, written once so every screen says the same
// thing about the same idea.

/**
 * One sentence per section, shown under its title by `HelpHint`. Keyed by the
 * route the menu links to, so a section added to the menu without a sentence
 * fails `glossary.test.ts` instead of reaching an operator unexplained.
 */
export const SECTION_HELP: Record<string, string> = {
  '/': 'Lo que espera al equipo hoy, la salud de la plataforma y sus cifras.',
  '/users': 'Cuentas, su estado y las medidas que se les han aplicado.',
  '/users/kyc': 'Verificaciones de identidad que un algoritmo no pudo decidir.',
  // No numbers here: the rule is two PlatformConfig keys, and the page header
  // states it with the values in force (ko-admin#269).
  '/users/strikes': 'Rechazos de anuncios que cuentan para la suspensión automática.',
  '/listings':
    'Anuncios que necesitan revisión antes de publicarse, y los que incumplen el plazo de envío.',
  '/stores':
    'Solicitudes del distintivo «Tienda verificada»: vendedores verificados que venden como tienda.',
  '/orders': 'Cada compra, desde el pago en garantía hasta la liberación al vendedor.',
  '/trades':
    'Intercambios retenidos por antifraude o por un problema, que necesitan a una persona.',
  '/kohub':
    'Ventas con KO Hub: KO recibe la pieza, la autentica y la envía al comprador con protección de KO.',
  '/disputes': 'Reclamaciones abiertas sobre un pedido o un intercambio, y sus apelaciones.',
  // Not «que un admin ha parado a mano», as the spec drafted it: the platform
  // also freezes by itself when a seller misses the shipment-proof deadline,
  // and those are most of this list.
  '/escrow':
    'Dinero en garantía que se ha parado —a mano o por un plazo de envío incumplido—, y recargas devueltas por el banco.',
  '/pending-decisions':
    'Dinero que la plataforma no ha podido colocar sola y espera a que alguien decida.',
  '/moderation': 'Denuncias de usuarios sobre un anuncio o sobre otra persona.',
  '/tournaments': 'Torneos de la Arena: aprobación, inscripción, juego y cierre.',
  '/tournaments/prizes': 'Premios asignados y su entrega, y los fondos que esperan liberación.',
  '/anticheat': 'Sesiones de quiz con señales de trampa, y las sanciones aplicadas.',
  '/questions': 'El banco de preguntas y su revisión.',
  '/question-banks': 'Bancos de preguntas y su árbol de categorías.',
  '/questions/proposals': 'Preguntas propuestas por creadores externos, pendientes de aprobar.',
  '/creators': 'Solicitudes para ser creador de preguntas, y sus sanciones.',
  '/koins':
    'Saldos de KOins por módulo y sus eventos; las políticas y los umbrales de nivel están en Configuración.',
  '/payouts': 'Retiros que han salido o intentan salir hacia la cuenta bancaria del usuario.',
  '/tiers': 'Nombres y arte de los diez niveles de cada módulo.',
  '/campaigns': 'Avisos masivos por push, correo o en la app.',
  '/referrals': 'Referidos en espera de cualificar, con la huella del alta.',
  '/reports': 'Métricas de la plataforma y exportaciones.',
  '/staff': 'Quién trabaja en el panel, con qué perfil y con qué nivel.',
  // A link, not a code: the invitee opens it and registers.
  '/invitations': 'Enlaces de un solo uso para dar de alta a un administrador.',
  '/taxonomy': 'Categorías y subcategorías del marketplace.',
  '/package-profiles': 'Medidas y peso con los que se cotizan las guías.',
  '/config':
    'Interruptores y valores con los que la plataforma decide sola; los cambios se aplican al momento.',
  '/audit':
    'Cada decisión sensible de la plataforma —dinero, sanciones, aprobaciones, configuración—, que no se puede editar ni borrar.',
  '/outbox': 'Eventos internos que no llegaron a su destino después de 5 intentos.',
  '/security': 'Tu segundo factor de acceso.',
  // The store owner's sections (2026-11-02): only their store, in their words.
  '/tienda':
    'Lo que espera de tu tienda hoy: pedidos por enviar, anuncios en revisión y piezas que KO Hub aguarda.',
  '/tienda/publicaciones': 'Tus anuncios: publicados, en revisión, rechazados y vendidos.',
  '/tienda/pedidos': 'Lo que te han comprado, desde el pago en garantía hasta que se libera.',
  '/tienda/kohub':
    'Tus ventas con KO Hub: KO recibe la pieza, la autentica y la envía al comprador.',
  '/tienda/cobros': 'Los retiros de tu saldo hacia tu cuenta bancaria.',
  '/tienda/perfil':
    'Los datos de tu tienda y las instrucciones de recogida que leen tus compradores.',
};

/**
 * The measures an operator can take on an account, one sentence each, so the
 * menu says what each does before anybody picks the one that merely reads
 * lighter.
 *
 * There is no «Desactivar»: it wrote exactly what «Banear» writes without
 * recording why, and the panel stopped offering it (ko-admin#151). And «Banear»
 * is not permanent, whatever the word suggests — «Reactivar» undoes it — so the
 * sentence says what really happens.
 */
export const ACCOUNT_MEASURES = {
  suspend: 'Temporal, con fecha de fin; el usuario puede apelar.',
  unsuspend: 'Devuelve el acceso antes de la fecha fijada.',
  ban: 'Cierra la cuenta y todas sus sesiones hasta que un admin la reactive; solo admins.',
  unban: 'Devuelve el acceso a una cuenta cerrada; solo admins.',
  logout: 'Le obliga a volver a entrar en todos sus dispositivos; no bloquea nada.',
} as const;
